import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Loader2, Check, BadgeDollarSign, RefreshCw, Ticket } from 'lucide-react';
import { apiClient } from '../../api/client';

const money = (cents) => `£${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/* Inline issue-license panel shown inside an expanded school row (CC → Schools). */
export const IssueLicenseInline = ({ school, onIssued }) => {
  const [suggest, setSuggest] = useState(null);
  const [tier, setTier] = useState('per_student');
  const [students, setStudents] = useState(275);
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiClient.request(`/api/school-licensing/admin/suggest/${school.school_id}`)
      .then((d) => { setSuggest(d); if (d.suggested_tier) setTier(d.suggested_tier); })
      .catch(() => {});
  }, [school.school_id]);

  const issue = async () => {
    if (!tier || busy) return;
    setBusy(true);
    try {
      const r = await apiClient.request(`/api/school-licensing/admin/issue/${school.school_id}`, {
        method: 'POST',
        body: JSON.stringify({ tier, student_count: tier === 'per_student' ? Number(students) : null, per_student_monthly: tier === 'per_student' ? Number(rate) : null }),
      });
      setResult({ ok: true, license: r.license, invoice: r.invoice });
      onIssued && onIssued();
    } catch (e) {
      setResult({ ok: false, error: e.message });
    }
    setBusy(false);
  };

  if (school.license_id && !result) {
    return (
      <p className="text-xs mt-3 font-semibold" style={{ color: '#059669' }} data-testid={`license-active-${school.school_id}`}>
        ✓ Licensed ({school.license_tier === 'city_license' ? 'City License' : 'Per-Student'}) — community code <span className="font-mono">{school.community_code}</span>
      </p>
    );
  }

  if (result?.ok) {
    return (
      <div className="mt-3 p-3 rounded-lg text-xs" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }} data-testid={`license-issued-${school.school_id}`}>
        <p className="font-bold" style={{ color: '#059669' }}>License issued + invoice {result.invoice.number} ({money(result.invoice.amount_cents)}, net 30) emailed.</p>
        <p className="mt-1" style={{ color: '#333' }}>Community family code: <span className="font-mono font-bold">{result.license.community_code}</span> (50% off for local families)</p>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }} data-testid={`issue-license-panel-${school.school_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <BadgeDollarSign size={14} style={{ color: '#F5831F' }} />
        <p className="text-xs font-bold" style={{ color: '#1A1209' }}>Issue 5-Year License</p>
        {suggest?.population ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }} data-testid={`population-badge-${school.school_id}`}>
            {suggest.city}: pop. {suggest.population.toLocaleString()} (Outreach Tracker)
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: '#1A1209' }}>
          <input type="radio" checked={tier === 'per_student'} onChange={() => setTier('per_student')} data-testid={`tier-perstudent-${school.school_id}`} />
          School License — special pricing, agreed per school · billed annually
        </label>
        {tier === 'per_student' && (
          <>
            <label className="flex items-center gap-1 text-[10px]" style={{ color: '#6B6257' }}>
              £/student/mo
              <input type="number" min={0.01} step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
                placeholder="agreed rate"
                className="w-24 px-2 py-1 border rounded text-xs" style={{ borderColor: '#E0E0E0' }}
                data-testid={`agreed-rate-${school.school_id}`} />
            </label>
            <label className="flex items-center gap-1 text-[10px]" style={{ color: '#6B6257' }}>
              students
              <input type="number" min={150} value={students} onChange={(e) => setStudents(e.target.value)}
                className="w-24 px-2 py-1 border rounded text-xs" style={{ borderColor: '#E0E0E0' }}
                data-testid={`student-count-${school.school_id}`} />
            </label>
          </>
        )}
        <button onClick={issue} disabled={!tier || busy || (tier === 'per_student' && !Number(rate))}
          className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: '#F5831F' }} data-testid={`issue-license-btn-${school.school_id}`}>
          {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Issue License + Invoice (net 30)'}
        </button>
        {tier === 'per_student' && Number(rate) > 0 && (
          <span className="text-[10px] font-semibold" style={{ color: '#0F8A4B' }}>
            = {money(Math.round(Number(rate) * 100) * 12 * Math.max(Number(students) || 0, 150))}/year
          </span>
        )}
      </div>
      {result?.error && <p className="text-xs mt-2 text-red-600">{result.error}</p>}
    </div>
  );
};

/* Licenses tab — CC → Schools → Licenses */
export const LicensesTab = () => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    apiClient.request('/api/school-licensing/admin/licenses').then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (invoiceId) => {
    setBusyId(invoiceId);
    try { await apiClient.request(`/api/school-licensing/admin/invoices/${invoiceId}/mark-paid`, { method: 'POST' }); load(); } catch (e) { alert(e.message); }
    setBusyId(null);
  };
  const renew = async (licenseId) => {
    setBusyId(licenseId);
    try { await apiClient.request(`/api/school-licensing/admin/licenses/${licenseId}/invoice-renewal`, { method: 'POST' }); load(); } catch (e) { alert(e.message); }
    setBusyId(null);
  };

  if (!data) return <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: '#F5831F' }} /></div>;

  return (
    <div data-testid="licenses-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { l: 'Active Licenses', v: data.totals.count, c: '#1A1209' },
          { l: 'Outstanding (net 30)', v: money(data.totals.outstanding_cents), c: '#D97706' },
          { l: 'Collected', v: money(data.totals.collected_cents), c: '#059669' },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }}>
            <p className="text-xs" style={{ color: '#6B6257' }}>{s.l}</p>
            <p className="text-2xl font-bold" style={{ color: s.c }}>{s.v}</p>
          </div>
        ))}
      </div>

      {data.licenses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl" style={{ border: '1px solid #E0E0E0' }}>
          <FileText size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#6B6257' }}>No licenses issued yet. Open a school in the Schools tab to issue its license after the 30-day beta.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.licenses.map((lic) => (
            <div key={lic.license_id} className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }} data-testid={`license-row-${lic.license_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{lic.school_name} <span className="font-normal text-xs" style={{ color: '#6B6257' }}>— {lic.city}, {lic.state}</span></h3>
                  <p className="text-xs mt-0.5" style={{ color: '#6B6257' }}>
                    {lic.tier_label} · {money(lic.annual_amount_cents)}/yr · {lic.billable_students} students · term ends {lic.term_end?.slice(0, 10)}
                  </p>
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#7B4FD8' }}>
                    <Ticket size={11} /> Community code: <span className="font-mono font-bold">{lic.community_code}</span> (families 50% off)
                  </p>
                </div>
                <button onClick={() => renew(lic.license_id)} disabled={busyId === lic.license_id}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ backgroundColor: '#F0F0F0', color: '#666' }}
                  data-testid={`renew-${lic.license_id}`}>
                  <RefreshCw size={10} className="inline mr-1" /> Issue Next Annual Invoice
                </button>
              </div>
              <div className="mt-3 space-y-1.5">
                {(lic.invoices || []).map((inv) => (
                  <div key={inv.invoice_id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: '#FAFAFA' }} data-testid={`invoice-row-${inv.invoice_id}`}>
                    <span style={{ color: '#1A1209' }} className="font-medium">{inv.number} — {money(inv.amount_cents)} — due {inv.due_at?.slice(0, 10)}</span>
                    {inv.status === 'paid' ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>PAID {inv.paid_at?.slice(0, 10)}</span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {inv.is_overdue && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }} data-testid={`overdue-badge-${inv.invoice_id}`}>OVERDUE</span>
                        )}
                        <button onClick={() => markPaid(inv.invoice_id)} disabled={busyId === inv.invoice_id}
                          className="px-3 py-1 rounded-lg text-[10px] font-semibold text-white" style={{ backgroundColor: '#059669' }}
                          data-testid={`mark-paid-${inv.invoice_id}`}>
                          <Check size={10} className="inline mr-0.5" /> Mark Paid
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
